import React, { useState, useEffect, useCallback } from "react";
import { Button } from "@fdoh/health-ui-react";
import { SoilProfile, SoilProfileHorizon, SoilProfileEditorProps } from "./AbandonmentForm.types";
import "./SoilProfileEditor.css";

export const SoilProfileEditor: React.FC<SoilProfileEditorProps> = ({
    profile,
    onSave,
    onCancel,
    isVisible,
    title,
    colorCodes,
    soilTextures,
    usdaSoilSeriesOptions
}) => {
    const [editingProfile, setEditingProfile] = useState<SoilProfile>({ ...profile });
    const [currentHorizon, setCurrentHorizon] = useState<SoilProfileHorizon>({
        horizonNumber: 1,
        munsellColor: "",
        colorDescription: "",
        soilTexture: "",
        depthFrom: 0,
        depthTo: 0
    });
    const [error, setError] = useState<string>("");
    const [isSubmitting, setIsSubmitting] = useState<boolean>(false);
    
    // Separate display values for depth inputs to handle typing properly
    const [depthFromDisplay, setDepthFromDisplay] = useState<string>("");
    const [depthToDisplay, setDepthToDisplay] = useState<string>("");
    
    // Track if user has started typing to enable auto-clear on first keystroke
    const [, setHasStartedTypingFrom] = useState<boolean>(false);
    const [, setHasStartedTypingTo] = useState<boolean>(false);

    // Helper function to renumber horizons sequentially
    const renumberHorizons = useCallback((horizons: SoilProfileHorizon[]): SoilProfileHorizon[] => {
        return horizons.map((horizon, index) => ({
            ...horizon,
            horizonNumber: index + 1
        }));
    }, []);



    // Initialize form for new horizon with smart defaults
    const initializeFormForNewHorizon = useCallback(() => {
        const lastHorizon = editingProfile.horizons[editingProfile.horizons.length - 1];
        const nextDepthFrom = lastHorizon ? lastHorizon.depthTo : 0;
        const nextDepthTo = nextDepthFrom + 1; // Smart range: always 1cm more than from
        
        setCurrentHorizon({
            horizonNumber: editingProfile.horizons.length + 1,
            munsellColor: "",
            colorDescription: "",
            soilTexture: "",
            depthFrom: nextDepthFrom,
            depthTo: nextDepthTo
        });
        
        // Set display values - show actual values including 0
        setDepthFromDisplay(nextDepthFrom.toString());
        setDepthToDisplay(nextDepthTo.toString());
        
        // Reset typing states
        setHasStartedTypingFrom(false);
        setHasStartedTypingTo(false);
        
        // Clear any previous errors
        setError("");
    }, [editingProfile.horizons]);

    // Initialize form when profile changes
    useEffect(() => {
        setEditingProfile({ ...profile });
        // Only initialize form if this is a completely new profile
        if (profile.horizons.length === 0 && !profile.id) {
            // Initialize form directly here to avoid circular dependency
            const nextDepthFrom = 0;
            const nextDepthTo = 1;
            
            setCurrentHorizon({
                horizonNumber: 1,
                munsellColor: "",
                colorDescription: "",
                soilTexture: "",
                depthFrom: nextDepthFrom,
                depthTo: nextDepthTo
            });
            
            setDepthFromDisplay("0");
            setDepthToDisplay("1");
            setHasStartedTypingFrom(false);
            setHasStartedTypingTo(false);
            setError("");
        }
    }, [profile]);

    // Validate horizon data
    const validateHorizon = useCallback((horizon: SoilProfileHorizon): boolean => {
        if (!horizon.munsellColor || horizon.munsellColor.trim() === "") {
            setError("Munsell Color is required");
            return false;
        }
        if (!horizon.soilTexture || horizon.soilTexture.trim() === "") {
            setError("Soil Texture is required");
            return false;
        }
        if (horizon.depthFrom >= horizon.depthTo) {
            setError("Depth To must be greater than Depth From");
            return false;
        }
        if (horizon.depthFrom < 0 || horizon.depthTo < 0) {
            setError("Depths must be positive numbers");
            return false;
        }
        if (horizon.depthFrom > 999 || horizon.depthTo > 999) {
            setError("Depths cannot exceed 999 cm");
            return false;
        }
        setError("");
        return true;
    }, []);

    // Validate entire profile before saving
    const validateProfile = useCallback((): boolean => {
        if (editingProfile.horizons.length === 0) {
            setError("At least one soil horizon is required");
            return false;
        }
        
        if (!editingProfile.usdaSoilSeries || editingProfile.usdaSoilSeries.trim() === "") {
            setError("USDA Soil Series is required when horizons exist");
            return false;
        }
        
        // Validate each horizon
        for (const horizon of editingProfile.horizons) {
            if (!validateHorizon(horizon)) {
                return false;
            }
        }
        
        setError("");
        return true;
    }, [editingProfile, validateHorizon]);

    // Handle adding or updating horizon
    const handleAddHorizon = useCallback(() => {
        if (!validateHorizon(currentHorizon)) {
            return;
        }

        let updatedHorizons: SoilProfileHorizon[];
        
        if (currentHorizon.id) {
            // Update existing horizon
            updatedHorizons = editingProfile.horizons.map(h => 
                h.id === currentHorizon.id ? currentHorizon : h
            );
        } else {
            // Add new horizon
            const newHorizon: SoilProfileHorizon = {
                ...currentHorizon
            };
            updatedHorizons = [...editingProfile.horizons, newHorizon];
        }
        
        // Renumber all horizons sequentially after any add/update
        updatedHorizons = renumberHorizons(updatedHorizons);
            
            setEditingProfile(prev => ({
                ...prev,
            horizons: updatedHorizons
        }));
        
        // Initialize form for next horizon with smart range
        const lastHorizon = updatedHorizons[updatedHorizons.length - 1];
        const nextDepthFrom = lastHorizon ? lastHorizon.depthTo : 0;
        const nextDepthTo = nextDepthFrom + 1;
        
        setCurrentHorizon({
            horizonNumber: updatedHorizons.length + 1,
            munsellColor: "",
            colorDescription: "",
            soilTexture: "",
            depthFrom: nextDepthFrom,
            depthTo: nextDepthTo
        });
        
        setDepthFromDisplay(nextDepthFrom.toString());
        setDepthToDisplay(nextDepthTo.toString());
        setHasStartedTypingFrom(false);
        setHasStartedTypingTo(false);
        setError("");
        
        // Simple focus after adding horizon
        setTimeout(() => {
            const firstField = document.querySelector('.soil-editor-overlay select[data-field="munsell"]') as HTMLSelectElement;
            if (firstField) {
                firstField.focus();
            }
        }, 100);

    }, [currentHorizon, editingProfile.horizons, validateHorizon, renumberHorizons]);

        // Handle deleting horizon
    const handleDeleteHorizon = useCallback((horizonNumber: number) => {
        // Remove horizon from the list by horizonNumber and renumber remaining
        setEditingProfile(prev => {
            const filteredHorizons = prev.horizons.filter(h => h.horizonNumber !== horizonNumber);
            return {
                ...prev,
                horizons: renumberHorizons(filteredHorizons)
            };
        });
        
        // Don't reinitialize form after deletion - let user continue working
        // The form will be ready for the next horizon when they're ready to add
    }, [renumberHorizons]);

    // Handle editing horizon
    const handleEditHorizon = useCallback((horizon: SoilProfileHorizon) => {
        setCurrentHorizon({ ...horizon });
        setDepthFromDisplay(horizon.depthFrom.toString());
        setDepthToDisplay(horizon.depthTo.toString());
        setHasStartedTypingFrom(false);
        setHasStartedTypingTo(false);
        setError("");
    }, []);

    // Handle USDA soil series change
    const handleUSDAChange = useCallback((value: string) => {
        setEditingProfile(prev => ({
            ...prev,
            usdaSoilSeries: value
        }));
        setError("");
    }, []);

    // Handle Munsell color change with auto-population
    const handleMunsellColorChange = useCallback((value: string) => {
        const selectedColor = colorCodes.find(code => code.name === value);
        const colorDescription = selectedColor?.description || '';
        
        setCurrentHorizon(prev => ({
            ...prev,
            munsellColor: value,
            colorDescription: colorDescription
        }));
    }, [colorCodes]);

    // Handle soil texture change
    const handleSoilTextureChange = useCallback((value: string) => {
        setCurrentHorizon(prev => ({
            ...prev,
            soilTexture: value
        }));
    }, []);

    // Handle depth input changes
    const handleDepthChange = useCallback((field: 'depthFrom' | 'depthTo', value: string) => {
        // Only allow numeric input
        if (value === '' || /^\d+$/.test(value)) {
            const numericValue = value === '' ? 0 : parseInt(value) || 0;
            
            if (field === 'depthFrom') {
                // Smart range: automatically adjust "to" depth to be 1cm more than "from"
                const newDepthTo = numericValue + 1;
                
                setCurrentHorizon(prev => ({
                    ...prev,
                    depthFrom: numericValue,
                    depthTo: newDepthTo
                }));
                
                setDepthFromDisplay(value);
                setDepthToDisplay(newDepthTo.toString());
            } else {
                // Manual "to" depth adjustment
                setCurrentHorizon(prev => ({
                    ...prev,
                    [field]: numericValue
                }));
                
                setDepthToDisplay(value);
            }
        }
    }, []);

    // Handle depth input focus
    const handleDepthFocus = useCallback((field: 'depthFrom' | 'depthTo') => {
        if (field === 'depthTo') {
            setDepthToDisplay('');
            setHasStartedTypingTo(true);
        } else {
            setHasStartedTypingFrom(true);
        }
    }, []);

    // Handle depth input blur
    const handleDepthBlur = useCallback((field: 'depthFrom' | 'depthTo') => {
        const currentValue = field === 'depthFrom' ? currentHorizon.depthFrom : currentHorizon.depthTo;
        const displayValue = currentValue.toString();
        
        if (field === 'depthFrom') {
            setDepthFromDisplay(displayValue);
        } else {
            setDepthToDisplay(displayValue);
        }
    }, [currentHorizon.depthFrom, currentHorizon.depthTo]);

    // Handle save and close
    const handleSaveAndClose = useCallback(async () => {
        if (!validateProfile()) {
            return;
        }
        
        setIsSubmitting(true);
        try {
            await onSave(editingProfile);
        } catch (error) {
            setError("Failed to save soil profile. Please try again.");
        } finally {
            setIsSubmitting(false);
        }
    }, [editingProfile, validateProfile, onSave]);

    // Handle cancel
    const handleCancel = useCallback(() => {
        setEditingProfile({ ...profile });
        initializeFormForNewHorizon();
        onCancel();
    }, [profile, initializeFormForNewHorizon, onCancel]);

    // Handle cancel edit
    const handleCancelEdit = useCallback(() => {
        initializeFormForNewHorizon();
    }, [initializeFormForNewHorizon]);

    // Simple Enter key handler - attached to the main form container
    const handleContainerKeyDown = (e: React.KeyboardEvent) => {
        if (e.key === 'Enter') {
            e.preventDefault();
            handleAddHorizon();
        }
    };

    if (!isVisible) return null;

    return (
        <div className="soil-editor-overlay" onKeyDown={handleContainerKeyDown}>
            <style>{`
                .section-header {
                    display: flex;
                    align-items: center;
                    gap: 16px;
                }
                
                
                
                .depth-inputs {
                    display: flex;
                    align-items: center;
                    gap: 8px;
                }
                
                .depth-separator {
                    color: #6b7280;
                    font-weight: 500;
                }
                
                .depth-input {
                    width: 80px;
                    text-align: center;
                }
                
                
            `}</style>
            <div className="soil-editor-container">
                {/* Header */}
                <div className="editor-header">
                    <div className="header-left">
                        <div className="header-icon">
                            <svg width="24" height="24" viewBox="0 0 24 24" fill="none">
                                <path d="M12 2L2 7L12 12L22 7L12 2Z" stroke="currentColor" strokeWidth="2" strokeLinejoin="round"/>
                                <path d="M2 17L12 22L22 17" stroke="currentColor" strokeWidth="2" strokeLinejoin="round"/>
                                <path d="M2 12L12 17L22 12" stroke="currentColor" strokeWidth="2" strokeLinejoin="round"/>
                            </svg>
                        </div>
                        <div className="header-text">
                            <h2 className="header-title">{title}</h2>
                            <p className="header-subtitle">Configure soil profile and horizons</p>
                        </div>
                    </div>
                    <button className="close-btn" onClick={handleCancel} disabled={isSubmitting}>
                        <svg width="20" height="20" viewBox="0 0 20 20" fill="none">
                            <path d="M15 5L5 15M5 5L15 15" stroke="currentColor" strokeWidth="2" strokeLinecap="round"/>
                        </svg>
                    </button>
                </div>

                {/* Content */}
                <div className="editor-content">
                    {/* USDA Soil Series */}
                    <div className="content-section">
                        <div className="section-header">
                            <div className="section-icon">
                                <svg width="20" height="20" viewBox="0 0 20 20" fill="none">
                                    <path d="M10 2L2 7L10 12L18 7L10 2Z" stroke="currentColor" strokeWidth="2"/>
                                    <path d="M2 17L10 22L18 17" stroke="currentColor" strokeWidth="2"/>
                                </svg>
                            </div>
                            <h3 className="section-title">Soil Series Classification</h3>
                        </div>
                        <div className="form-field">
                            <label className="field-label">
                                USDA Classification
                                <span className="required-indicator">*</span>
                            </label>
                            <select
                                className={`field-select ${!editingProfile.usdaSoilSeries || editingProfile.usdaSoilSeries.trim() === '' ? 'field-error' : ''}`}
                                value={editingProfile.usdaSoilSeries || ""}
                                onChange={(e) => handleUSDAChange(e.target.value)}
                                disabled={isSubmitting}
                            >
                                <option value="">Select soil series</option>
                                {usdaSoilSeriesOptions.map(option => (
                                    <option key={option.id} value={option.name}>
                                        {option.name}
                                    </option>
                                ))}
                            </select>
                            
                        </div>
                    </div>

                    {/* Current Horizons */}
                    <div className="content-section">
                        <div className="section-header">
                            <div className="section-icon">
                                <svg width="20" height="20" viewBox="0 0 20 20" fill="none">
                                    <rect x="3" y="3" width="14" height="14" rx="2" stroke="currentColor" strokeWidth="2"/>
                                    <path d="M3 9H17M9 3V17" stroke="currentColor" strokeWidth="2"/>
                                </svg>
                            </div>
                            <h3 className="section-title">Soil Horizons</h3>
                            <span className="horizon-badge">{editingProfile.horizons.length}</span>
                        </div>
                        
                        {editingProfile.horizons.length > 0 ? (
                            <div className="horizons-table-container">
                                <table className="horizons-table">
                                    <thead>
                                        <tr>
                                            <th className="th-horizon" style={{width: '80px'}}>Horizon</th>
                                            <th className="th-depth" style={{width: '100px'}}>Depth (cm)</th>
                                            <th className="th-color" style={{width: '140px'}}>Munsell Color</th>
                                            <th className="th-texture" style={{width: '140px'}}>Soil Texture</th>
                                            <th className="th-description" style={{width: 'auto'}}>Description</th>
                                            <th className="th-actions" style={{width: '100px'}}>Actions</th>
                                    </tr>
                                </thead>
                                <tbody>
                                    {editingProfile.horizons.map((horizon, index) => (
                                            <tr key={horizon.id || `temp-${index}`}>
                                                <td className="td-horizon">
                                                    <span className="horizon-badge">
                                                        {horizon.horizonNumber}
                                                    </span>
                                                </td>
                                                <td className="td-depth">
                                                    <span className="depth-range">
                                                        {horizon.depthFrom} - {horizon.depthTo}
                                                    </span>
                                                </td>
                                                <td className="td-color">
                                                    <span className="color-chip">
                                                        {horizon.munsellColor || 'Not specified'}
                                                    </span>
                                                </td>
                                                <td className="td-texture">
                                                    <span className="texture-chip">
                                                        {horizon.soilTexture || 'Not specified'}
                                                    </span>
                                                </td>
                                                <td className="td-description">
                                                    <span className="description-text">
                                                        {horizon.colorDescription || '—'}
                                                    </span>
                                                </td>
                                                <td className="td-actions">
                                                    <div className="action-buttons">
                                                        <button
                                                            className="action-btn edit"
                                                    onClick={() => handleEditHorizon(horizon)}
                                                            disabled={isSubmitting}
                                                        >
                                                            <svg width="16" height="16" viewBox="0 0 16 16" fill="none">
                                                                <path d="M11 1H5C4.45 1 4 1.45 4 2V14C4 14.55 4.45 15 5 15H11C11.55 15 12 14.55 12 14V2C12 1.45 11.55 1 11 1Z" stroke="currentColor" strokeWidth="1.5"/>
                                                                <path d="M8 5H6M8 8H6M8 11H6M10 5H10.01M10 8H10.01M10 11H10.01" stroke="currentColor" strokeWidth="1.5"/>
                                                            </svg>
                                                        </button>
                                                        <button
                                                            className="action-btn delete"
                                                            onClick={() => handleDeleteHorizon(horizon.horizonNumber)}
                                                            disabled={isSubmitting}
                                                        >
                                                            <svg width="16" height="16" viewBox="0 0 16 16" fill="none">
                                                                <path d="M2 4H14M5.5 4V2.5C5.5 2.22 5.72 2 6 2H10C10.28 2 10.5 2.22 10.5 2.5V4M12.5 4V13.5C12.5 13.78 12.28 14 12 14H4C3.72 14 3.5 13.78 3.5 13.5V4H12.5Z" stroke="currentColor" strokeWidth="1.5"/>
                                                            </svg>
                                                        </button>
                                                    </div>
                                            </td>
                                        </tr>
                                    ))}
                                </tbody>
                            </table>
                            </div>
                        ) : (
                            <div className="empty-state">
                                <div className="empty-icon">
                                    <svg width="48" height="48" viewBox="0 0 48 48" fill="none">
                                        <rect x="8" y="8" width="32" height="32" rx="4" stroke="currentColor" strokeWidth="2" strokeDasharray="4 4"/>
                                        <path d="M24 20V28M20 24H28" stroke="currentColor" strokeWidth="2"/>
                                    </svg>
                                </div>
                                <h4 className="empty-title">No horizons yet</h4>
                                <p className="empty-description">Add your first soil horizon below to get started</p>
                            </div>
                        )}
                        </div>

                    {/* Add New Horizon */}
                    <div className="content-section">
                        <div className="section-header">
                            <div className="section-icon">
                                <svg width="20" height="20" viewBox="0 0 20 20" fill="none">
                                    <path d="M10 5V15M5 10H15" stroke="currentColor" strokeWidth="2" strokeLinecap="round"/>
                                </svg>
                            </div>
                            <h3 className="section-title">
                                {currentHorizon.id ? `Edit Horizon ${currentHorizon.horizonNumber}` : `Add Horizon ${editingProfile.horizons.length + 1}`}
                            </h3>
                        </div>
                        
                        <div className="form-grid">
                            <div className="form-field">
                                <label className="field-label">
                                    Munsell Color
                                    <span className="required-indicator">*</span>
                                </label>
                                    <select
                                    className="field-select"
                                    data-field="munsell"
                                        value={currentHorizon.munsellColor}
                                    onChange={(e) => handleMunsellColorChange(e.target.value)}
                                        required
                                    disabled={isSubmitting}
                                    >
                                    <option value="">Select color</option>
                                        {colorCodes.map(code => (
                                            <option key={code.id} value={code.name}>{code.name}</option>
                                        ))}
                                    </select>
                                </div>
                            
                            <div className="form-field">
                                <label className="field-label">
                                    Color Description
                                    <span className="required-indicator">*</span>
                                </label>
                                    <input
                                        type="text"
                                    className="field-input"
                                        value={currentHorizon.colorDescription}
                                    readOnly
                                    placeholder="Auto-populated from color selection"
                                        required
                                    style={{ backgroundColor: '#f8f9fa', cursor: 'not-allowed' }}
                                    disabled={isSubmitting}
                                    />
                                </div>
                            
                            <div className="form-field">
                                <label className="field-label">
                                    Soil Texture
                                    <span className="required-indicator">*</span>
                                </label>
                                    <select
                                    className="field-select"
                                        value={currentHorizon.soilTexture}
                                    onChange={(e) => handleSoilTextureChange(e.target.value)}
                                        required
                                    disabled={isSubmitting}
                                    >
                                    <option value="">Select texture</option>
                                        {soilTextures.map(texture => (
                                            <option key={texture.id} value={texture.name}>{texture.name}</option>
                                        ))}
                                    </select>
                                </div>
                            
                            <div className="form-field depth-field">
                                                                 <label className="field-label">
                                     Depth Range (cm)
                                     <span className="required-indicator">*</span>
                                 </label>
                                <div className="depth-inputs">
                                            <input
                                        type="text"
                                        className="field-input depth-input"
                                        data-field="depthFrom"
                                        value={depthFromDisplay}
                                        onChange={(e) => handleDepthChange('depthFrom', e.target.value)}
                                        onFocus={() => handleDepthFocus('depthFrom')}
                                        onBlur={() => handleDepthBlur('depthFrom')}
                                        placeholder="From"
                                                required
                                        disabled={isSubmitting}
                                            />
                                    <span className="depth-separator">to</span>
                                            <input
                                        type="text"
                                        className="field-input depth-input"
                                        value={depthToDisplay}
                                        onChange={(e) => handleDepthChange('depthTo', e.target.value)}
                                        onFocus={() => handleDepthFocus('depthTo')}
                                        onBlur={() => handleDepthBlur('depthTo')}
                                        placeholder="To"
                                                required
                                        disabled={isSubmitting}
                                            />
                                        </div>
                                
                                </div>
                            </div>
                            
                            {error && (
                            <div className="error-banner">
                                <svg width="20" height="20" viewBox="0 0 20 20" fill="none">
                                    <path d="M10 1L18 17H2L10 1Z" stroke="currentColor" strokeWidth="2"/>
                                    <path d="M10 7V11M10 15H10.01" stroke="currentColor" strokeWidth="2"/>
                                </svg>
                                <span>{error}</span>
                                </div>
                            )}
                            
                                                 <div className="form-actions">
                            {currentHorizon.id && (
                                <Button
                                    className="btn-secondary"
                                    onClick={handleCancelEdit}
                                    disabled={isSubmitting}
                                    style={{ marginRight: '10px' }}
                                >
                                    Cancel Edit
                                </Button>
                            )}
                            <Button
                                className="add-horizon-btn"
                                onClick={handleAddHorizon}
                                disabled={isSubmitting}
                            >
                                <svg width="20" height="20" viewBox="0 0 20 20" fill="none">
                                    <path d="M10 5V15M5 10H15" stroke="currentColor" strokeWidth="2" strokeLinecap="round"/>
                                </svg>
                                {currentHorizon.id ? 'Update Horizon' : 'Add Horizon'}
                            </Button>
                        </div>
                    </div>
                </div>
                    
                {/* Footer */}
                <div className="editor-footer">
                            <Button
                        className="btn-secondary" 
                                onClick={handleCancel}
                        disabled={isSubmitting}
                            >
                                Cancel
                            </Button>
                            <Button
                        className="btn-primary" 
                                onClick={handleSaveAndClose}
                        disabled={isSubmitting}
                    >
                        <svg width="20" height="20" viewBox="0 0 20 20" fill="none">
                            <path d="M17 3H3C2.45 3 2 3.45 2 4V16C2 16.55 2.45 17 3 17H17C17.55 17 18 16.55 18 16V4C18 3.45 17.55 3 17 3Z" stroke="currentColor" strokeWidth="2"/>
                            <path d="M7 7H13M7 11H13M7 15H13" stroke="currentColor" strokeWidth="2"/>
                        </svg>
                        {isSubmitting ? 'Saving...' : 'Save & Close'}
                            </Button>
                        </div>
            </div>
        </div>
    );
};

export default SoilProfileEditor;
